import React, { useEffect, useState } from "react";
import "../PaymentDetails/PaymentDetails.css";
import CartPartialPayment from "./CartPartialPayment";
import CartFullPayment from "./CartFullPayment";
import { useLocation, useNavigate } from "react-router-dom";
import NavigationButtons from "../../../Template/NavigationButtons/NavigationButtons";
import { validateCoupon } from "../../../utils/APIs/cartApis";
import { toast } from "react-toastify";

const CartPaymentDetails = () => {
  const [paymentMethod, setPaymentMethod] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const [checkoutData, setCheckoutData] = useState(() => location.state || {});
  const serviceCost = checkoutData.finalPayable || 0;

  const MAX_PAYMENT_LIMIT = 1000000; // ₹10 Lakhs
  const SAFE_MAX_AMOUNT = 450000; // ₹4.5 Lakhs

  const calculatePartialAmount = (totalAmount) => {
    let amountToPay = totalAmount;
    if (amountToPay > SAFE_MAX_AMOUNT) {
      let basisAmount = totalAmount;
      let slicesNeeded = Math.ceil(basisAmount / SAFE_MAX_AMOUNT);
      let dynamicStepAmount = Math.ceil(basisAmount / slicesNeeded);
      amountToPay = Math.min(amountToPay, dynamicStepAmount, SAFE_MAX_AMOUNT);
    }
    return amountToPay;
  };

  const partialAmount = calculatePartialAmount(serviceCost);

  useEffect(() => {
    if (!checkoutData.finalPayable) {
      navigate(-1);
    }
  }, [checkoutData, navigate]);

  useEffect(() => {
    if (serviceCost > MAX_PAYMENT_LIMIT && paymentMethod === "full") {
      setPaymentMethod("partial");
    }
  }, [serviceCost, paymentMethod]);

  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handleApplyPromoCode = async (promoCode, setCouponLoading) => {
    if (!promoCode) {
        toast.error("Please enter a promo code");
        return;
    }

    try {
        setCouponLoading(true);
        const items = checkoutData.items || [];
        const baseAmount = checkoutData.subtotal || 0; // subtotal is total without discount

        let couponType = "Service"; // Default
        if (items.length > 0) {
            const category = items[0].category?.toLowerCase();
            if (category === "editing") couponType = "PhotoEditing";
            else if (category === "shoot_team") couponType = "HourlyShoot";
            else if (["duration", "package", "custom"].includes(category)) couponType = "CustomizeBooking";
            else if (category === "service") couponType = "Service";
        }

        let eligibleAmount = 0;
        items.forEach(item => {
            const cat = item.category?.toLowerCase();
            if (couponType === "HourlyShoot" && cat === "shoot_team") {
                eligibleAmount += item.price * (item.quantity || 1);
            } else if (couponType === "PhotoEditing" && cat === "editing") {
                eligibleAmount += item.price * (item.quantity || 1);
            } else if (couponType === "CustomizeBooking" && ["duration", "package", "custom"].includes(cat)) {
                eligibleAmount += item.price * (item.quantity || 1);
            } else if (couponType === "Service" && cat === "service") {
                eligibleAmount += item.price * (item.quantity || 1);
            }
        });

        if (eligibleAmount === 0) eligibleAmount = baseAmount;

        const res = await validateCoupon({
            code: promoCode,
            amount: eligibleAmount,
            couponType: couponType
        });

        if (res?.data?.success) {
            const appliedCoupon = res.data.coupon;
            
            // Calculate new finalPayable
            let currentDiscount = 0;
            let eligibleSubtotal = 0;

            items.forEach(item => {
                const cat = item.category?.toLowerCase();
                let isEligible = false;

                if (appliedCoupon.couponType === "HourlyShoot") {
                    isEligible = (cat === "shoot_team");
                } else if (appliedCoupon.couponType === "PhotoEditing") {
                    isEligible = (cat === "editing");
                } else if (appliedCoupon.couponType === "Service") {
                    isEligible = (cat === "service");
                } else if (appliedCoupon.couponType === "CustomizeBooking") {
                    isEligible = ["duration", "package", "custom"].includes(cat);
                } else {
                    isEligible = true;
                }

                if (isEligible) {
                    eligibleSubtotal += item.price * (item.quantity || 1);
                }
            });

            if (eligibleSubtotal === 0) eligibleSubtotal = baseAmount;

            if (appliedCoupon.discountType === "percentage") {
                currentDiscount = (eligibleSubtotal * appliedCoupon.discountValue) / 100;
            } else if (appliedCoupon.discountType === "flat") {
                currentDiscount = Math.min(appliedCoupon.discountValue, eligibleSubtotal);
            }

            const finalBaseAmount = baseAmount - currentDiscount;
            const convenienceFee = Number((finalBaseAmount * 0.02).toFixed(2));
            const gstAmount = Number((finalBaseAmount * 0.18).toFixed(2));
            const finalPayable = Number(Math.floor(finalBaseAmount + convenienceFee + gstAmount));

            setCheckoutData(prev => ({
                ...prev,
                appliedCoupon: appliedCoupon,
                couponApplied: true,
                currentDiscount: currentDiscount,
                convenienceFee: convenienceFee,
                finalPayable: finalPayable
            }));
            
            toast.success(res.data.message || "Promo code applied successfully!");
        } else {
            toast.error(res?.data?.message || "Invalid promo code");
        }
    } catch (error) {
        console.error("Error applying promo code:", error);
        toast.error(error?.response?.data?.message || "Failed to apply promo code");
    } finally {
        if (setCouponLoading) setCouponLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
      const baseAmount = checkoutData.subtotal || 0;
      const convenienceFee = Number((baseAmount * 0.02).toFixed(2));
      const gstAmount = Number((baseAmount * 0.18).toFixed(2));
      const finalPayable = Number(Math.floor(baseAmount + convenienceFee + gstAmount));

      setCheckoutData(prev => ({
          ...prev,
          appliedCoupon: null,
          couponApplied: false,
          currentDiscount: 0,
          convenienceFee: convenienceFee,
          finalPayable: finalPayable
      }));
      toast.info("Promo code removed");
  };

  return (
    <div className="payment-details-container" style={{ position: 'relative' }}>
      <NavigationButtons />
      <div className="payment-details-content">
        <h1 className="payment-title">Payment Details</h1>
        <p className="payment-subtitle">Review your payment before proceeding</p>

        <div className="booking-amount-section">
          <span className="amount-label">Booking Amount</span>
          <span className="amount-value">₹{serviceCost}</span>
        </div>

        <div className="payment-divider"></div>

        <h2 className="payment-method-title">How would you like to pay ?</h2>

        <div className="payment-options">
          <label className="payment-option">
            <input
              type="radio"
              name="paymentMethod"
              value="partial"
              checked={paymentMethod === "partial"}
              onChange={handlePaymentChange}
            />
            <span className="radio-custom"></span>
            <span className="option-label">
              Partial Payment
              {serviceCost > MAX_PAYMENT_LIMIT && (
                <small className="payment-note"> (₹{partialAmount} payable now)</small>
              )}
            </span>
          </label>

          <label className="payment-option">
            <input
              type="radio"
              name="paymentMethod"
              value="full"
              checked={paymentMethod === "full"}
              onChange={handlePaymentChange}
              disabled={serviceCost > MAX_PAYMENT_LIMIT}
            />
            <span className="radio-custom"></span>
            <span className="option-label">
              Full Payment
              {serviceCost > MAX_PAYMENT_LIMIT && (
                <small className="payment-note"> (Disabled above ₹10,00,000)</small>
              )}
            </span>
          </label>
        </div>

        {paymentMethod === "partial" && (
          <CartPartialPayment checkoutData={checkoutData} onApplyPromoCode={handleApplyPromoCode} onRemovePromoCode={handleRemovePromoCode} />
        )}

        {paymentMethod === "full" && (
          <CartFullPayment checkoutData={checkoutData} onApplyPromoCode={handleApplyPromoCode} onRemovePromoCode={handleRemovePromoCode} />
        )}
      </div>
    </div>
  );
};

export default CartPaymentDetails;
